"use client";

// packages/observability/src/web.ts
// Browser-side OpenTelemetry init. OTel 2.x: spanProcessors are passed to the
// WebTracerProvider constructor (the legacy addSpanProcessor() was removed).

import {
  WebTracerProvider,
  BatchSpanProcessor,
} from "@opentelemetry/sdk-trace-web";
import {
  defaultResource,
  resourceFromAttributes,
} from "@opentelemetry/resources";
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME } from "@opentelemetry/semantic-conventions/incubating";
import {
  BatchLogRecordProcessor,
  LoggerProvider,
} from "@opentelemetry/sdk-logs";
import { logs } from "@opentelemetry/api-logs";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { propagation } from "@opentelemetry/api";
import {
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
  CompositePropagator,
} from "@opentelemetry/core";
import type { Attributes } from "@opentelemetry/api";

import { makeTraceExporter, makeLogExporter } from "./exporters";
import { buildFetchIgnoreUrls } from "./fetch-ignore";
import { logger } from "./logger";
import {
  ContextEnrichingLogProcessor,
  ContextEnrichingSpanProcessor,
  drainEarlyErrorBuffer,
} from "./web-context";
import { AttentionOnlySpanProcessor } from "./sampling-span-processor";
import {
  TokenRedactingLogRecordProcessor,
  TokenRedactingSpanProcessor,
} from "./token-redaction";
import { installBrowserFlushOnHide } from "./browser-flush";
import { withCircuitBreaker } from "./exporter-circuit-breaker";

let started = false;

// Browser keepalive requests are capped at 64 KiB. Keep export batches small
// enough to survive navigation instead of allowing the SDK default (512
// records) to form an unload request the browser rejects before it starts.
const BROWSER_MAX_EXPORT_BATCH_SIZE = 4;
const BROWSER_MAX_QUEUE_SIZE = 512;

// Export timeout MUST stay strictly below the schedule interval. When they were
// equal (both 10s, until 2026-08-14) a hung export occupied one of the browser's
// per-origin connection slots 100% of the time: it timed out at exactly the
// moment the next batch was due, so there was never a gap. A 2026-08-14 HAR of
// /finances caught it — 12 `/api/otlp/traces` aborts at exactly 10.00s on exact
// 10s centers, while page data queued behind them. Keeping the timeout at 40% of
// the interval leaves the slot free most of the time; the circuit breaker below
// handles the sustained-outage case. Telemetry never outbids the app for the
// network, so a slow export is dropped rather than allowed to run long.
const BROWSER_TRACE_SCHEDULE_MS = 10_000;
const BROWSER_TRACE_TIMEOUT_MS = 4_000;
const BROWSER_LOG_SCHEDULE_MS = 5_000;
const BROWSER_LOG_TIMEOUT_MS = 2_000;

export interface InitBrowserOptions {
  serviceName: string;
  endpoint: string;
  ingestToken: string;
  serviceVersion?: string;
  /** Deployment-wide resource attributes for both browser traces and logs. */
  attributes?: Attributes;
}

export function initBrowserObservability(opts: InitBrowserOptions): void {
  if (started) {
    return;
  }
  if (typeof window === "undefined") {
    return;
  }

  // Pass endpoint + headers explicitly to the exporters. Writing to
  // process.env on the client forces Next.js to load its `process` polyfill
  // into the client bundle, and Turbopack's HMR registry occasionally loses
  // that polyfill's module factory between refreshes (manifests as
  // "module factory is not available" at this file).
  // Skip the Authorization header entirely when no real token is supplied —
  // the same-origin OTLP proxy injects the real token server-side, so the
  // browser exporter doesn't need to attach a placeholder bearer (which would
  // ride along on every OTLP POST as dead weight).
  const exporterOverrides: {
    endpoint: string;
    headers?: Record<string, string>;
  } = {
    endpoint: opts.endpoint,
  };
  if (opts.ingestToken && opts.ingestToken !== "proxy") {
    exporterOverrides.headers = { Authorization: `Bearer ${opts.ingestToken}` };
  }

  const resource = defaultResource().merge(
    resourceFromAttributes({
      ...opts.attributes,
      [ATTR_SERVICE_NAME]: opts.serviceName,
      [ATTR_SERVICE_VERSION]: opts.serviceVersion ?? "unknown",
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: process.env.NODE_ENV ?? "production",
    }),
  );

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [
      // Stamp workspace_id / user_id / posthog_session_id onto every span at
      // start time so SigNoz trace search can pivot to a PostHog replay.
      // Runs BEFORE the attention-only filter — even spans we drop don't need
      // the join keys, but the processor is cheap and keeps the wiring simple.
      new ContextEnrichingSpanProcessor(),
      // Signing/share tokens ride in page and fetch URLs (http.url, url.full,
      // the route stamped above): rewrite them out before anything exports.
      new TokenRedactingSpanProcessor(),
      new AttentionOnlySpanProcessor({
        slowMs: 1000,
        inner: [
          new BatchSpanProcessor(
            withCircuitBreaker(makeTraceExporter(exporterOverrides)),
            {
              maxQueueSize: BROWSER_MAX_QUEUE_SIZE,
              maxExportBatchSize: BROWSER_MAX_EXPORT_BATCH_SIZE,
              scheduledDelayMillis: BROWSER_TRACE_SCHEDULE_MS,
              exportTimeoutMillis: BROWSER_TRACE_TIMEOUT_MS,
            },
          ),
        ],
      }),
    ],
  });
  // The default stack context manager. Never ZoneContextManager: Zone.js replaces
  // the global Promise, and its `resolve` hands back React's Flight chunk (which
  // inherits Promise.prototype) instead of wrapping it. That chunk's `then`
  // returns undefined, so the first call of every bound Server Action came back
  // undefined while its POST still ran: a signed contract showed "Something went
  // wrong" on every browser (found 2026-11-04). Only fetch and document-load
  // spans run here, and Zone cannot follow native async/await anyway.
  tracerProvider.register();

  // ContextEnrichingLogProcessor runs first so it stamps user_id /
  // workspace_id / route attributes onto each record BEFORE the batch
  // processor enqueues it for export. Order is significant: OTel processes
  // synchronously through the array and BatchLogRecordProcessor takes a
  // snapshot of the record on enqueue.
  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new ContextEnrichingLogProcessor(),
      new TokenRedactingLogRecordProcessor(),
      new BatchLogRecordProcessor({
        exporter: withCircuitBreaker(makeLogExporter(exporterOverrides)),
        maxQueueSize: BROWSER_MAX_QUEUE_SIZE,
        maxExportBatchSize: BROWSER_MAX_EXPORT_BATCH_SIZE,
        scheduledDelayMillis: BROWSER_LOG_SCHEDULE_MS,
        exportTimeoutMillis: BROWSER_LOG_TIMEOUT_MS,
      }),
    ],
  });
  logs.setGlobalLoggerProvider(loggerProvider);

  propagation.setGlobalPropagator(
    new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator(),
      ],
    }),
  );

  registerInstrumentations({
    // UserInteractionInstrumentation deliberately omitted: every click
    // produced a root span, which (combined with no head-sampler) was the
    // dominant ingest source into ClickHouse. Page loads and fetches give
    // enough signal without it.
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        // Only inject traceparent/baggage into our own backends. Sending these
        // headers to third parties (Stripe, Google Maps, etc.) breaks their
        // CORS preflight when they don't list traceparent in allowed-headers.
        propagateTraceHeaderCorsUrls: [
          /\.atriam\.ai/,
          /\.atriamflowbuild\.com/,
        ],
        // Includes the OTLP proxy endpoint itself — exports are fetches, and
        // spanning them would feed each export back into the next batch.
        ignoreUrls: buildFetchIgnoreUrls(opts.endpoint),
      }),
    ],
  });

  // Replay any errors the inline shim captured before the SDK booted.
  // Safe to call even if the shim never installed (no-op when buffer empty).
  drainEarlyErrorBuffer(logger);

  // Start a final keepalive-backed drain before navigation/backgrounding.
  // The helper coalesces visibilitychange + pagehide so one transition cannot
  // double-send the same queued records.
  installBrowserFlushOnHide([tracerProvider, loggerProvider]);

  started = true;
}
